import { Button } from '@src/components/ui/button'
import { Checkbox } from '@src/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@src/components/ui/popover'
import { ScrollArea, ScrollBar } from '@src/components/ui/scroll-area'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useCallback, useEffect, useState, type MouseEvent } from 'react'

interface RiskProfileHazardSelectorProps {
  label: string
  placeholder: string
  options: string[]
  selected: string[]
  onChange: (selected: string[]) => void
  disabled?: boolean
  getLabel?: (option: string) => string
}

const RiskProfileHazardSelector = ({
  label,
  placeholder,
  options,
  selected,
  onChange,
  disabled = false,
  getLabel,
}: RiskProfileHazardSelectorProps) => {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState<string[]>(selected)

  const resolveLabel = useCallback((option: string) => getLabel?.(option) ?? option, [getLabel])

  useEffect(() => {
    if (open) setPending(selected)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const count = selected.length
  const total = options.length

  const triggerLabel =
    count === 0
      ? placeholder
      : count === total
        ? `All ${label.toLowerCase()}`
        : count === 1
          ? resolveLabel(selected[0])
          : `${count} selected`

  const toggleItem = useCallback((item: string) => {
    setPending((current) =>
      current.includes(item)
        ? current.filter((selectedItem) => selectedItem !== item)
        : [...current, item],
    )
  }, [])

  const clearAll = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setPending([])
  }

  const selectAll = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setPending(options)
  }

  const handleApply = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onChange(pending)
    setOpen(false)
  }

  const pendingCount = pending.length
  const pendingTotal = options.length

  return (
    <div className="flex min-w-[220px] flex-1 flex-col gap-1">
      <label className="text-xs font-medium text-foreground">{label}</label>

      <Popover open={open} onOpenChange={(nextOpen) => !disabled && setOpen(nextOpen)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="h-10 w-full justify-between rounded-lg border border-border bg-background px-3 text-sm font-normal shadow-sm hover:bg-background/80"
          >
            <span className="truncate text-foreground">{triggerLabel}</span>

            {open ? (
              <ChevronUp className="ml-2 h-4 w-4 shrink-0 text-foreground" />
            ) : (
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-foreground" />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-full max-w-[420px] rounded-xl p-2"
        >
          <ScrollArea className="h-[280px] rounded-md pr-3">
            <div className="space-y-1 py-1 pr-2">
              {options.map((option) => {
                const checked = pending.includes(option)

                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => toggleItem(option)}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
                  >
                    <Checkbox
                      checked={checked}
                      aria-label={resolveLabel(option)}
                      className="pointer-events-none"
                    />

                    <span className="min-w-0 flex-1 truncate">{resolveLabel(option)}</span>
                  </button>
                )
              })}
            </div>

            <ScrollBar className="w-2 rounded-full bg-muted/60 p-0.5" />
          </ScrollArea>

          <div className="mt-2 flex items-center justify-between gap-x-2 border-t px-3 py-3">
            <span className="text-xs text-foreground">
              {pendingCount} of {pendingTotal} selected
            </span>

            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={clearAll}
                className="font-semibold text-muted-foreground hover:text-foreground"
              >
                Clear all
              </Button>

              <Button variant="ghost" size="sm" type="button" onClick={selectAll}>
                Select all
              </Button>
            </div>
          </div>

          <Button onClick={handleApply} className="mt-2 w-full">
            Apply
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default RiskProfileHazardSelector
